import React, { useEffect, useState, useContext, useRef } from 'react';
import styled from 'styled-components';
import { FirebaseContext } from 'context';
import { EditorState, convertToRaw, ContentState } from 'draft-js';
import { Questions, SelectedQuestion, Button, Question } from 'components';
import { PlusIcon } from 'assets/svgs';
import { useLocation } from '@reach/router';
import { useWindowSize } from 'hooks';
import queryString from 'query-string';
import draftToHtml from 'draftjs-to-html';
import { motion, AnimatePresence, LayoutGroup } from 'motion/react';
import { scrollToAnchor, generateNanoId } from 'utils';
import ToolbarDropdown from './ToolbarDropdown';

// eslint-disable-next-line react/jsx-no-useless-fragment, react/function-component-definition
let Editor = () => <></>;
let htmlToDraft = null;

const wrapperStyle = {
  height: '7.5rem'
};

const toolbarStyle = {
  height: '2rem',
  padding: '0.313rem 0.25rem'
};

const localization = {
  locale: 'en',
  translations: {
    'components.controls.image.dropFileText': 'Drop an image or click to upload'
  }
};

const editorStyle = ({ editorIsFocused, addingQuestion }) => ({
  background: editorIsFocused ? 'rgba(226, 228, 234, 0.40)' : '#fff',
  color: 'var(--dark-blue)',
  border: 'none',
  borderRadius: editorIsFocused ? 'var(--border-radius-sm)' : 0,
  fontFamily: 'Inter, sans-serif',
  fontSize: '1.25rem',
  fontWeight: 600,
  height: '100%',
  minHeight: '12.18rem',
  // maxHeight: '16.375rem',
  outline: 'none',
  overflow: 'auto',
  padding: '2.32rem 1.125rem 3.75rem',
  wordWrap: 'break-word',
  wordBreak: 'break-word'
});

// Always return full toolbar config - will hide visually on mobile with CSS
const getToolbarOptions = ({ uploadCallback }) => ({
  options: ['inline', 'link', 'image'],
  inline: {
    inDropdown: false,
    options: ['bold', 'italic', 'underline'],
    bold: { className: 'react-draft-editor-icon' },
    italic: { className: 'react-draft-editor-icon' },
    underline: { className: 'react-draft-editor-icon' }
  },
  link: {
    inDropdown: false,
    options: ['link'],
    defaultTargetOption: '_blank',
    link: { className: 'react-draft-editor-icon' },
    unlink: { className: 'react-draft-editor-icon' }
  },
  image: {
    inDropdown: false,
    uploadCallback,
    previewImage: true,
    urlEnabled: false,
    uploadEnabled: true,
    alignmentEnabled: false,
    alt: { present: false },
    defaultSize: {
      height: 'auto',
      width: 'auto'
    },
    inputAccept: 'image/gif,image/jpeg,image/jpg,image/png,image/svg',
    className: 'react-draft-editor-icon'
  },
  emoji: {
    inDropdown: false
  },
  history: {
    inDropdown: false,
    options: ['undo', 'redo']
  }
});

// Preserve empty paragraphs by adding non-breaking space
const preserveEmptyParagraphs = (html) =>
  html.replace(/<p><\/p>/g, '<p>&nbsp;</p>');

// Remove trailing empty paragraphs from the end
const removeTrailingEmptyParagraphs = (html) =>
  html.replace(/(<p>(&nbsp;|\s*)<\/p>\s*)+$/g, '');

function SessionReview({ sessionId, questions, isModerator: isModeratorProp }) {
  const { firebase, user } = useContext(FirebaseContext);
  const isModerator = isModeratorProp ?? user?.isModerator?.includes(sessionId);
  const uid = firebase?.auth?.currentUser?.uid;
  const [users, setUsers] = useState([]);
  const [editorState, setEditorState] = useState(EditorState.createEmpty());
  const [selectedQuestion, setSelectedQuestion] = useState(null);
  const [questionIdQueryParam, setQuestionIdQueryParam] = useState(null);
  const [addingQuestion, setAddingQuestion] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [newQuestionOrder, setNewQuestionOrder] = useState(null);
  const [pendingQuestionId, setPendingQuestionId] = useState(null);
  const [isCancelling, setIsCancelling] = useState(false);
  const [editorIsFocused, setEditorIsFocused] = useState(false);
  const location = useLocation();
  const { width } = useWindowSize();
  const { search } = location;
  const queryParams = queryString.parse(search);

  // Check if editor has any content (text, images, or atomic blocks)
  const currentContent = editorState?.getCurrentContent();
  const hasText = currentContent?.getPlainText().trim().length > 0;
  const hasAtomicBlocks = currentContent
    ?.getBlockMap()
    .some((block) => block.getType() === 'atomic');
  const editorContent = hasText || hasAtomicBlocks;

  const [isEditorReady, setIsEditorReady] = useState(false);

  const editorRef = useRef(null);
  const prevAddingQuestionRef = useRef(false);
  const prevSelectedQuestionRef = useRef(null);

  useEffect(() => {
    // eslint-disable-next-line global-require
    Editor = require('react-draft-wysiwyg').Editor;
    // eslint-disable-next-line global-require
    htmlToDraft = require('html-to-draftjs').default;
    setIsEditorReady(true);
  }, []);

  useEffect(() => {
    if (queryParams?.questionId) {
      setQuestionIdQueryParam(queryParams.questionId);
    }
  }, []);

  useEffect(() => {
    // Only clear editor when transitioning from false to true
    if (addingQuestion && !prevAddingQuestionRef.current) {
      setEditorState(EditorState.createEmpty());
      editorRef.current.focusEditor();
    }
    prevAddingQuestionRef.current = addingQuestion;
  }, [addingQuestion]);

  useEffect(() => {
    // Only clear editor when a new question is selected
    if (
      selectedQuestion &&
      selectedQuestion !== prevSelectedQuestionRef.current
    ) {
      setEditorState(EditorState.createEmpty());
      setAddingQuestion(false);
    }
    prevSelectedQuestionRef.current = selectedQuestion;
  }, [selectedQuestion]);

  useEffect(() => {
    if (questionIdQueryParam && questions.length) {
      const _selectedQuestion = questions.find(
        (question) => question.id === questionIdQueryParam
      );
      setSelectedQuestion(_selectedQuestion);
    }
  }, [questions, questionIdQueryParam]);

  useEffect(() => {
    if (selectedQuestion && !questionIdQueryParam) {
      scrollToAnchor({
        anchor: 'selected-question',
        offset: -84,
        duration: 100
      });
    }
  }, [selectedQuestion]);

  const uploadImageCallback = async (file) => {
    try {
      if (!firebase?.storageFactory) {
        throw new Error('Storage factory not initialized');
      }

      const downloadURL = await firebase.storageFactory.uploadQuestionImage(
        file,
        sessionId
      );

      return { data: { link: downloadURL } };
    } catch (error) {
      console.error('Error uploading image:', error);
      throw error;
    }
  };

  const handleCancelQuestion = () => {
    setIsCancelling(true);
    setTimeout(() => setAddingQuestion(false), 0);
    setTimeout(() => setIsCancelling(false), 600);
  };

  const handleEditQuestion = (question) => {
    setEditingQuestion(question);
    // Convert HTML back to EditorState for editing
    if (htmlToDraft) {
      const contentBlock = htmlToDraft(question.html);
      if (contentBlock) {
        const contentState = ContentState.createFromBlockArray(
          contentBlock.contentBlocks
        );
        const newEditorState = EditorState.createWithContent(contentState);
        const editorStateWithFocus = EditorState.moveFocusToEnd(newEditorState);
        setEditorState(editorStateWithFocus);
      }
    }
    // Focus editor after a brief delay
    setTimeout(() => editorRef.current?.focusEditor(), 100);
  };

  const handleCancelEdit = () => {
    setEditingQuestion(null);
    setEditorState(EditorState.createEmpty());
  };

  const handleSaveEdit = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (editingQuestion) {
      const rawHtml = draftToHtml(
        convertToRaw(editorState.getCurrentContent())
      );
      const html = removeTrailingEmptyParagraphs(
        preserveEmptyParagraphs(rawHtml)
      );

      await firebase.questions.editQuestion({
        sessionId,
        questionId: editingQuestion.id,
        html
      });

      setEditingQuestion(null);
      setEditorState(EditorState.createEmpty());
    }
  };

  const handleAddQuestion = async () => {
    const rawHtml = draftToHtml(convertToRaw(editorState.getCurrentContent()));
    const html = removeTrailingEmptyParagraphs(
      preserveEmptyParagraphs(rawHtml)
    );
    const questionId = generateNanoId({ length: 6 });
    setPendingQuestionId(questionId);

    await firebase.questions.addQuestion({
      sessionId,
      questionId,
      html,
      order: newQuestionOrder,
      isVisible: true
    });

    setTimeout(() => setAddingQuestion(false), 0);
    // setAddingQuestion(false);
    setEditorState(EditorState.createEmpty());
  };

  const handleToggleQuestionVisibility = async (
    questionId,
    currentVisibility
  ) => {
    try {
      await firebase.questions.updateQuestionVisibility({
        sessionId,
        questionId,
        isVisible: !currentVisibility
      });
    } catch (error) {
      console.error('Error toggling question visibility:', error);
    }
  };

  useEffect(() => {
    if (firebase?.misc) {
      firebase.misc.getAllUsers().then((res) => {
        if (!res.empty) {
          const _users = [];
          res.docs.forEach((doc) => {
            _users.push({
              id: doc.id,
              name: doc.data().name,
              email: doc.data().email,
              isModerator: doc.data().isModerator,
              sessionsUserHasAccessTo: doc.data().sessionsUserHasAccessTo
            });
          });
          setUsers(_users);
        }
      });
    }
  }, [firebase]);

  useEffect(() => {
    if (!addingQuestion) return;

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        handleCancelQuestion();
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [addingQuestion]);

  useEffect(() => {
    if (!editingQuestion) return;

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        handleCancelEdit();
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [editingQuestion]);

  return selectedQuestion ? (
    <SelectedQuestion
      selectedQuestion={selectedQuestion}
      setSelectedQuestion={setSelectedQuestion}
      setQuestionIdQueryParam={setQuestionIdQueryParam}
      users={users}
      sessionId={sessionId}
    />
  ) : questions.length ? (
    <QuestionsContainer isModerator={user?.isModerator?.includes(sessionId)}>
      <h3>Welcome to the Congress Feedback Program</h3>
      <p>
        You can use this space to share your thoughts, reflections, and
        takeaways from the San Antonio Breast Cancer Symposium 2025.
        <br />
        <br />
        {sessionId === 'x0ffd4' && (
          <span>
            NOTE: Other HCPs&apos; comments beneath a question will be revealed
            only after leaving your own comment.
          </span>
        )}
      </p>
      {user?.isModerator?.includes(sessionId) && (
        <Button
          disabled={addingQuestion}
          icon={<PlusIcon width="1.25rem" />}
          backgroundColor="var(--blue)"
          onClick={() => {
            setNewQuestionOrder(questions.length + 1);
            setAddingQuestion(true);
          }}>
          Add Question
        </Button>
      )}
      <LayoutGroup>
        <AnimatePresence
          mode="popLayout"
          onExitComplete={() => {
            setPendingQuestionId(null);
          }}>
          {addingQuestion && (
            <Question
              addingQuestion={addingQuestion}
              isCancelling={isCancelling}
              newQuestionOrder={newQuestionOrder}
              setAddingQuestion={setAddingQuestion}
              setEditorIsFocused={setEditorIsFocused}
              firebase={firebase}
              sessionId={sessionId}>
              {isEditorReady && (
                <Editor
                  editorState={editorState}
                  wrapperClassName="react-draft-wrapper"
                  toolbarClassName="react-draft-toolbar"
                  editorClassName="react-draft-editor"
                  // wrapperStyle={wrapperStyle}
                  toolbarStyle={toolbarStyle}
                  editorStyle={editorStyle({
                    editorIsFocused,
                    addingQuestion
                  })}
                  onEditorStateChange={setEditorState}
                  toolbar={getToolbarOptions({
                    uploadCallback: uploadImageCallback
                  })}
                  toolbarCustomButtons={
                    width < 550
                      ? [
                          <ToolbarDropdown
                            editorState={editorState}
                            onChange={setEditorState}
                          />
                        ]
                      : []
                  }
                  ref={editorRef}
                  placeholder="Type your question here"
                  localization={localization}
                  stripPastedStyles
                />
              )}
              <AddQuestionButtonWrapper>
                <Button
                  onClick={handleCancelQuestion}
                  backgroundColor="var(--red)">
                  Cancel
                </Button>
                <Button disabled={!editorContent} onClick={handleAddQuestion}>
                  Save
                </Button>
              </AddQuestionButtonWrapper>
            </Question>
          )}
        </AnimatePresence>
        {(() => {
          const visibleQuestions = isModerator
            ? questions
            : questions.filter((q) => q.isVisible);

          return (
            <motion.div
              layout="position"
              transition={
                addingQuestion || isCancelling ? undefined : { duration: 0 }
              }>
              <Questions
                Editor={Editor}
                editingQuestion={editingQuestion}
                editorContent={editorContent}
                editorIsFocused={editorIsFocused}
                editorRef={editorRef}
                editorState={editorState}
                editorStyle={editorStyle}
                firebase={firebase}
                getToolbarOptions={getToolbarOptions}
                handleCancelEdit={handleCancelEdit}
                handleEditQuestion={handleEditQuestion}
                handleSaveEdit={handleSaveEdit}
                handleToggleQuestionVisibility={handleToggleQuestionVisibility}
                isCancelling={isCancelling}
                isEditorReady={isEditorReady}
                isModerator={isModerator}
                localization={localization}
                pendingQuestionId={pendingQuestionId}
                questions={visibleQuestions}
                sessionId={sessionId}
                setEditorIsFocused={setEditorIsFocused}
                setEditorState={setEditorState}
                setSelectedQuestion={setSelectedQuestion}
                toolbarStyle={toolbarStyle}
                uid={uid}
                uploadImageCallback={uploadImageCallback}
                width={width}
              />
            </motion.div>
          );
        })()}
      </LayoutGroup>
    </QuestionsContainer>
  ) : null;
}

const AddQuestionButtonWrapper = styled.div`
  bottom: 1.188rem;
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
  position: absolute;
  right: 1.188rem;
`;

const QuestionsContainer = styled(motion.div).attrs({
  initial: {
    opacity: 0
  },
  animate: {
    opacity: 1
  }
})`
  background: #fff;
  border-radius: var(--border-radius-md);
  margin: 0 1.25rem -2.25rem;
  padding: 1.95rem 0.563rem 1.125rem;
  position: relative;
  top: -6rem;

  > button {
    font-weight: 400;
    margin: 0.65rem 0 2.125rem 1.438rem;
    padding: 0.65em 1.5em 0.65em 0.95em;

    circle {
      fill: var(--blue);
    }
  }

  h4 {
    color: var(--blue);
    font-family: 'Inter', sans-serif;
    font-size: 0.875rem;
    font-weight: 700;
    text-transform: uppercase;
  }

  > h3 {
    font-size: 1.5rem;
    font-weight: 600;
    line-height: 120%;
    margin-bottom: 1.05em;
  }

  > p {
    line-height: 120%;
    margin-bottom: 1.85rem;
    max-width: 720px;

    span {
      color: var(--blue);
      font-weight: 700;
    }
  }

  > h3,
  > p {
    padding: 0 1.438rem;
  }

  p {
    margin: 0.75em 0 ${({ isModerator }) => (isModerator ? '1em' : '2.6em')};
  }

  @media (min-width: 1200px) {
    margin: 0;
    grid-column: 2/9;
  }

  .public-DraftStyleDefault-block {
    margin: 0.75em 0 1em !important;
    word-wrap: break-word !important;
    word-break: break-word !important;
    overflow-wrap: break-word !important;
    line-height: 120% !important;
  }
  .react-draft-editor-icon {
    background-color: var(--blue) !important;
  }

  .react-draft-editor-icon img {
    filter: brightness(0) invert(1) !important;
  }

  .react-draft-editor-icon:hover {
    box-shadow: 0.063rem 0.063rem 0 var(--dark-blue) !important;
  }

  .react-draft-editor-icon.rdw-option-active {
    background-color: var(--purple) !important;
    box-shadow: none !important;
    ${'' /* filter: brightness(114%); */}
  }

  .react-draft-editor-icon[title='Link'] {
    margin: 0 2px;

    @media (min-width: 390px) {
      margin: 0 4px;
    }
  }

  .rdw-link-modal {
    background-color: var(--blue) !important;
    border: 1px solid #f1f1f1 !important;
    box-shadow: 3px 3px 5px #bfbdbd !important;
    height: 176px !important;
  }

  .rdw-inline-wrapper,
  .rdw-link-wrapper,
  .rdw-emoji-wrapper,
  .rdw-image-wrapper {
    margin-bottom: 0 !important;
  }

  .rdw-link-modal-btn:not(:disabled) {
    background-color: #fff !important;
    border: 1px solid #f1f1f1 !important;
    color: #000 !important;
  }

  .rdw-link-modal-btn:disabled {
    cursor: not-allowed;
    background-color: #ece9e9 !important;
    border: 1px solid #f1f1f1 !important;
    color: rgba(000, 000, 000, 0.3) !important;
  }

  .rdw-link-modal-label {
    color: #fff !important;
  }

  /* Hides the 'open in seperate window?' box in the link modal */
  .rdw-link-modal-target-option {
    display: none !important;
  }

  .rdw-link-modal #linkTitle,
  .rdw-link-modal #linkTarget {
    color: var(---brown) !important;

    &:focus {
      background: #fff !important;
    }
  }

  /* Links */
  .rdw-link-decorator-wrapper a {
    text-decoration: underline !important;
    text-decoration-color: var(--purple) !important;

    span {
      color: var(--purple) !important;
    }
  }

  .rdw-link-decorator-icon {
    background-color: transparent !important;
    filter: invert(1);
  }

  /* Placeholder text */
  .public-DraftEditorPlaceholder-root {
    color: var(--light-brown) !important;
    font-family: 'Inter', sans-serif !important;
    font-size: 1.25rem !important;
    font-style: italic !important;
    font-weight: 400 !important;
    ${'' /* opacity: 0.65 !important; */}
  }

  .rdw-link-modal-buttonsection {
    margin-top: 9px !important;
  }

  /* Image modal - hide alt text, size fields, and tabs */
  .rdw-image-modal-alt-input,
  .rdw-image-modal-alt-lbl,
  .rdw-image-modal-size,
  .rdw-image-modal-size-input,
  .rdw-image-modal-header {
    display: none !important;
  }

  /* Center align image upload text */
  .rdw-image-modal-upload-option-label {
    text-align: center !important;
  }

  /* Style image modal to match link modal */
  .rdw-image-modal {
    background-color: var(--blue) !important;
    border: 1px solid #f1f1f1 !important;
    box-shadow: 3px 3px 5px #bfbdbd !important;
    width: 270px !important;
  }

  .rdw-image-modal-btn:not(:disabled) {
    background-color: #fff !important;
    border: 1px solid #f1f1f1 !important;
    color: #000 !important;
  }

  .rdw-image-modal-btn:disabled {
    cursor: not-allowed;
    background-color: #ece9e9 !important;
    border: 1px solid #f1f1f1 !important;
    color: rgba(000, 000, 000, 0.3) !important;
  }

  .rdw-image-modal-upload-option {
    background-color: #fff !important;
    border: 2px dashed var(--light-grey) !important;
    color: #000 !important;
  }

  /* Hide image alignment controls */
  .rdw-image-alignment-options-popup {
    display: none !important;
  }

  /* Force images to be left-aligned */
  .rdw-image-left,
  .rdw-image-center,
  .rdw-image-right,
  .rdw-image-imagewrapper {
    text-align: left !important;
    display: block !important;
    margin-left: 0 !important;
    margin-right: auto !important;
  }

  /* Set max-width for uploaded images when in editor */
  .rdw-image-imagewrapper img {
    margin-bottom: 1.05rem !important;
    max-width: 100% !important;
    height: auto !important;
    display: block !important;
  }

  .rdw-image-imagewrapper figure {
    margin-bottom: 1.05rem !important;
    max-width: 100% !important;
    height: auto !important;
    display: block !important;
  }

  .react-draft-toolbar {
    background-color: transparent !important;
    border: transparent !important;
    border-radius: 18px !important;
    padding: 0 !important;
    position: absolute;
    bottom: 14px;
    left: 15px;
    z-index: 3 !important;

    /* Hide default toolbar controls on mobile (but keep them accessible for programmatic clicking) */
    @media (max-width: 549px) {
      .rdw-inline-wrapper,
      .rdw-link-wrapper,
      .rdw-image-wrapper {
        visibility: hidden !important;
        position: absolute !important;
        pointer-events: none !important;
      }

      /* Force modals to be visible and positioned correctly */
      .rdw-link-modal,
      .rdw-image-modal {
        visibility: visible !important;
        position: fixed !important;
        left: 50% !important;
        top: 50% !important;
        transform: translate(-50%, -50%) !important;
        pointer-events: auto !important;
        z-index: 1000 !important;
      }
    }
  }

  .rdw-option-wrapper {
    cursor: pointer !important;
    height: 25px !important;
    width: 25px !important;
    border-radius: 50% !important;
    min-width: unset !important;
    padding: 2px !important;

    &[title='Bold'] img {
      width: 10px;
    }

    &[title='Italic'] img {
      width: 15px;
      margin-left: 1px;
    }

    &[title='Underline'] img {
      width: 16px;
      margin-left: 1px;
      margin-top: 1px;
    }

    &[title='Link'] img {
      width: 15px;
      margin-top: 1px;
      /* margin-left: 1px; */
    }

    &[title='Image'] {
      img {
        display: none;
      }

      &::before {
        content: '';
        display: block;
        width: 16px;
        height: 16px;
        margin-bottom: 1px;
        margin-left: 1px;
        background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3E%3Crect x='1.5' y='2.5' width='17' height='15' rx='1.5' stroke='%23fff' stroke-width='2'/%3E%3Ccircle cx='6' cy='7' r='2' fill='%23fff'/%3E%3Cpath d='M1.5 14l4-5 4 4 3-4 5 6v2.5a1.5 1.5 0 0 1-1.5 1.5h-14a1.5 1.5 0 0 1-1.5-1.5V14z' fill='%23fff' stroke='%23fff' stroke-width='0.5'/%3E%3C/svg%3E");
        background-size: contain;
        background-repeat: no-repeat;
        background-position: center;
      }
    }

    ${
      '' /* &[title="Emoji"] img {
      opacity: 0 !important;
    } */
    }
  }

  .rdw-dropdown-selectedtext {
    padding: 0 10px !important;
    ${'' /* z-index: 101 !important; */}

    img {
      filter: brightness(0) invert(1) !important;
    }
  }

  .rdw-inline-dropdown {
    background-color: var(--blue) !important;
    border-radius: var(--border-radius-lg) !important;
    height: 24px !important;
    width: 40px !important;
    position: relative !important;
    top: 3px !important;
  }

  .rdw-dropdown-carettoopen,
  .rdw-dropdown-carettoclose {
    right: 14% !important;
    filter: brightness(0) invert(1) !important;
    scale: 0.85 !important;
  }

  .rdw-dropdown-optionwrapper {
    overflow-y: auto !important;
    width: 86% !important;
    margin: 0 auto !important;
    border: none !important;
    border-bottom-left-radius: 12px !important;
    border-bottom-right-radius: 12px !important;
    top: -3px !important;

    padding-top: 3px !important;
    background-color: var(--blue) !important;
    position: relative !important;
    z-index: -1 !important;

    li[title='Bold']:hover,
    li[title='Italic']:hover,
    li[title='Underline']:hover {
      background-color: var(--dark-blue) !important;
    }

    .rdw-dropdownoption-active {
      background-color: var(--purple) !important;
    }
  }

  .react-draft-wrapper {
    position: relative;
  }

  .react-draft-editor {
    font-family: 'Inter', sans-serif !important;
    font-weight: 400 !important;
    word-wrap: break-word !important;
    word-break: break-word !important;
    overflow-wrap: break-word !important;

    &::-webkit-scrollbar {
      display: none !important;
    }
  }

  .DraftEditor-editorContainer {
    z-index: 2 !important;
  }

  .rdw-suggestion-wrapper {
    ::-webkit-scrollbar {
      width: 0.55rem !important;
    }

    ::-webkit-scrollbar-track {
      box-shadow: inset 0 0 0.31rem grey !important;
      border-radius: 0.625rem !important;
    }

    ::-webkit-scrollbar-thumb {
      background-color: var(--blue) !important;
      border-radius: 0.625rem !important;
    }
  }

  .rdw-suggestion-dropdown {
    border: 1px solid var(--blue) !important;
    border-radius: 5px !important;
  }

  .rdw-suggestion-option {
    cursor: pointer;
    padding: 7px 12px 7px 5px !important;
    white-space: nowrap !important;

    &:hover {
      background-color: var(--light-blue) !important;
      color: var(--blue) !important;
    }
  }

  .rdw-mention-link {
    background-color: var(--light-blue) !important;
    color: var(--blue) !important;
    font-weight: bold;
  }

  .wysiwyg-mention {
    color: var(--blue);
    font-weight: bold;
    pointer-events: none !important;
    text-decoration: none;
  }
`;

export default SessionReview;
